/**
 * Product-analytics tracking for the Resource Matrix app.
 *
 * PHI CONTRACT (HIPAA): events are workforce-behavior telemetry, never patient
 * data. To keep the pipeline PHI-free by design this module:
 *   - stamps the sanitized route pattern (getSafeRoutePattern), never the raw
 *     URL — raw paths embed athenaPatientId and free-text search queries;
 *   - never sends free text (search boxes get a length, never the query);
 *   - never sends the user agent (an identifier);
 *   - sends the Auth0 sub only so the SERVER can HMAC it — the raw id is never
 *     logged (see server.mjs buildProductEventLogEntry).
 * The server re-sanitizes and key-allowlists everything as defense-in-depth; if
 * this layer drifts, the server is the backstop. Keep both in sync.
 */

import { getUserIdFromToken } from './getUserIdFromToken';
import { getSafeApiUrl, getSafeRoutePattern } from './sanitization';
import { getAnonymousSessionId } from './telemetrySession';

interface AnalyticsEvent {
  event: string;
  properties: Record<string, unknown>;
  route: string;
  sessionId: string;
  timestamp: number;
  userId?: string;
}

// Generic product-analytics dimensions. Every new event is one `product_action`
// with an (action, resource) pair, so the Cloud Monitoring dashboard and the
// weekly slides both aggregate with a single GROUP BY resource, action — no
// per-event bespoke queries. Extend these unions as new verbs/resources land.
export type ProductAction =
  | 'view'
  | 'search'
  | 'list'
  | 'create'
  | 'update'
  | 'delete'
  | 'restore'
  | 'export'
  | 'step';

export type ProductResource =
  | 'practice'
  | 'provider'
  | 'market'
  | 'plan'
  | 'insurance'
  | 'followup'
  | 'office_alert'
  | 'daily_alert'
  | 'absence'
  | 'closure'
  | 'map'
  | 'global';

export const PRODUCT_ACTION_EVENT = 'product_action';

// getUserIdFromToken() returns the sentinel 'unknown-user' when no token is
// present (early boot / unauthenticated). Treat that as "not resolved yet" so
// the lazy refresh in track() re-resolves once the real token lands, instead of
// permanently attributing events to the fake id.
function resolveUserId(): string | null {
  const id = getUserIdFromToken();
  return id && id !== 'unknown-user' ? id : null;
}

const ENDPOINT = '/telemetry/events';
const FLUSH_INTERVAL_MS = 5_000;
// server.mjs caps the batch at 20 and the body at 16KB; keep them aligned.
const MAX_BATCH = 20;

class Analytics {
  private queue: AnalyticsEvent[] = [];
  private userId: string | null = null;
  private started = false;

  constructor() {
    this.userId = resolveUserId();
    this.start();
  }

  private start() {
    // flush() is a no-op outside PROD, so there's no reason to register a timer
    // or listener in dev/test (avoids open handles that hang test runners).
    if (this.started || typeof window === 'undefined' || !import.meta.env.PROD) {
      return;
    }
    this.started = true;
    // Flush periodically and on the last chance before the tab goes away.
    setInterval(this.flush, FLUSH_INTERVAL_MS);
    window.addEventListener('pagehide', this.flush);
  }

  /**
   * Track an event with properties. Route and session are stamped here so every
   * event — including heartbeats — is attributable to a screen without the
   * caller having to think about PHI.
   */
  track(event: string, properties: Record<string, unknown> = {}) {
    // Refresh the user id lazily; the token may not have been present at boot.
    if (!this.userId) {
      this.userId = resolveUserId();
    }

    this.queue.push({
      event,
      properties,
      route: getSafeRoutePattern(),
      sessionId: getAnonymousSessionId(),
      timestamp: Date.now(),
      userId: this.userId || undefined,
    });

    if (this.queue.length >= MAX_BATCH) {
      this.flush();
    }
  }

  /**
   * Generic product-analytics event — the single shape the dashboard and the
   * weekly slides aggregate on. Prefer this over the bespoke per-resource
   * methods below for all new instrumentation.
   *
   * PHI: props must be counts / enums / ids only — NEVER free text. Pass
   * `queryLength`, never the query; the server key-allowlist drops anything it
   * doesn't recognize, and sanitizes the rest.
   */
  trackEvent(
    action: ProductAction,
    resource: ProductResource,
    props: Record<string, unknown> = {}
  ) {
    // Spread props FIRST so a caller can't override the action/resource
    // dimensions the dashboard groups by.
    this.track(PRODUCT_ACTION_EVENT, { ...props, action, resource });
  }

  private flush = () => {
    // Only ship from production builds; dev/test keep events in-memory for
    // inspection via getEvents()/exportEvents().
    if (!this.queue.length || !import.meta.env.PROD) {
      return;
    }

    const batch = this.queue.splice(0, MAX_BATCH);
    const body = JSON.stringify({
      events: batch,
      app: {
        environment:
          import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'unknown',
        gitSha: import.meta.env.VITE_GIT_SHA || 'local',
      },
    });

    // keepalive lets the request survive tab close (mirrors coreWebVitals).
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {
      // Telemetry must never break the app.
    });
  };

  /**
   * Track plan details row edit events
   */
  trackPlanDetailsRowEditOpened(planId: string, providerId: string) {
    this.track('plan_details_row_edit_opened', {
      planId,
      providerId,
    });
  }

  trackPlanDetailsRowEditSaved(
    planId: string,
    providerId: string,
    changedFields: string[],
    durationMs: number
  ) {
    this.track('plan_details_row_edit_saved', {
      planId,
      providerId,
      changedFields,
      durationMs,
    });
  }

  trackNavigateToBulkEditor(planId: string, providerId?: string, from?: string) {
    this.track('navigate_to_bulk_editor', {
      planId,
      providerId,
      from,
    });
  }

  trackPaginationChanged(planId: string, page: number, pageSize: number) {
    this.track('pagination_changed', {
      planId,
      page,
      pageSize,
    });
  }

  trackFiltersChanged(planId: string, filters: Record<string, string>) {
    // PHI-safe: record WHICH filters were active, never their values.
    this.track('filters_changed', {
      planId,
      filterKeys: Object.keys(filters),
    });
  }

  trackSearchUsed(planId: string, searchQuery: string) {
    // PHI-safe: staff type names/MRNs into search boxes — record length only.
    this.track('search_used', {
      planId,
      queryLength: searchQuery.length,
    });
  }

  trackInsuranceEditOpened(providerId: string, source: string) {
    this.track('insurance_edit_opened', {
      providerId,
      source,
    });
  }

  trackInsuranceEditSaved(providerId: string, changedFields: string[]) {
    this.track('insurance_edit_saved', {
      providerId,
      changedFields,
    });
  }

  trackBlockedRoutePlanDetails(referrer: string) {
    // Callers pass window.location.href; reduce it to a safe path (no query,
    // no ids) before it leaves the browser.
    this.track('blocked_route_plan_details', {
      referrer: getSafeApiUrl(referrer) ?? 'unknown',
    });
  }

  /**
   * Get queued (not-yet-flushed) events — for debugging in dev/test.
   */
  getEvents(): AnalyticsEvent[] {
    return [...this.queue];
  }

  /**
   * Clear queued events.
   */
  clearEvents() {
    this.queue = [];
  }

  /**
   * Export queued events as JSON for analysis.
   */
  exportEvents(): string {
    return JSON.stringify(this.queue, null, 2);
  }
}

// Export singleton instance
export const analytics = new Analytics();

// Export types for use in components
export type { AnalyticsEvent };
